import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export const InterProcesoGerente = () => {
  const toast = useToast();
  const { auth } = useAuth();

  // =========================
  // Estado y hooks
  // =========================
  const [ticajes, setTicajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // =========================
  // Carga de datos
  // =========================
  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    if (!auth?.id) return;

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

      const [resTicajes, resUsuarios] = await Promise.all([
        fetch(`${Global.url}ticajes/proceso`, { headers }),
        fetch(`${Global.url}usuarios`, { headers }),
      ]);

      const dataUsuarios = resUsuarios.ok ? await resUsuarios.json() : [];
      setUsuarios(Array.isArray(dataUsuarios) ? dataUsuarios : []);

      if (!resTicajes.ok) {
        toast.error("No se pudieron obtener los ticajes en proceso.");
        setTicajes([]);
        return;
      }

      const dataTicajes = await resTicajes.json();
      const ticajesAll = Array.isArray(dataTicajes) ? dataTicajes : [];

      // Filtrar por equipo del gerente
      const idsEquipo = new Set(
        (Array.isArray(dataUsuarios) ? dataUsuarios : [])
          .filter((u) => String(u.gerente_id) === String(auth.id))
          .map((u) => String(u.id))
      );

      const ticajesEquipo = ticajesAll.filter((t) => idsEquipo.has(String(t.usuario_id)));
      setTicajes(ticajesEquipo);

      if (!resUsuarios.ok) {
        toast.warning("No se pudieron cargar los usuarios (se mostrarán IDs).");
      }
    } catch (error) {
      console.error("Error al cargar los ticajes:", error);
      toast.error("Error de conexión con el servidor.");
      setTicajes([]);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Utilidades
  // =========================
  const getNombreCompleto = (usuario_id) => {
    const u = usuarios.find((x) => String(x.id) === String(usuario_id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${usuario_id ?? "-"}`;
  };

  const fmtDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // =========================
  // Rows / Columns
  // =========================
  const rows = useMemo(
    () =>
      (ticajes || []).map((t) => ({
        ...t,
        usuario_nombre: t.usuario || getNombreCompleto(t.usuario_id),
        inicio_txt: fmtDate(t.inicio),
        comentarios_txt: t.comentarios?.trim() ? t.comentarios : "Sin comentarios",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticajes, usuarios]
  );

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1, minWidth: 260 },
    ],
    []
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Ticajes en Proceso
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay ticajes activos de tu equipo."}
                </Box>
              ),
            }}
          />
        </Paper>
      </Stack>
    </Box>
  );
};
